import { useState, useEffect, useCallback } from 'react';
import { StorageService } from '../services/storage';
import { userPreferencesService } from '../services/userPreferencesService';
import { useAuthStore } from '../store/authStore';

export type UserMode = 'simple' | 'expert';

// Custom event for user mode changes
const USER_MODE_CHANGE_EVENT = 'user-mode-change';

/**
 * Hook to manage user mode preference (simple vs expert)
 * Persists the choice in localStorage and syncs to database for authenticated users
 */
export function useUserMode(): [UserMode, (mode: UserMode) => void] {
  const { user, isAuthenticated } = useAuthStore();
  const [userMode, setUserModeState] = useState<UserMode>(() => {
    // Default to 'simple' mode for new users
    return StorageService.get<UserMode>('user_mode', 'simple');
  });

  // Sync with database when user logs in
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      userPreferencesService.syncAfterLogin(user.id).then(() => {
        // Update local state with preference from database
        userPreferencesService.getPreference('userMode').then((mode) => {
          if (mode && mode !== userMode) {
            setUserModeState(mode);
            // Dispatch event to sync across all components
            window.dispatchEvent(new CustomEvent(USER_MODE_CHANGE_EVENT, { detail: mode }));
          }
        });
      });
    } else if (!isAuthenticated) {
      userPreferencesService.clearCache();
    }
  }, [isAuthenticated, user?.id]);

  // Listen for mode changes from other components
  useEffect(() => {
    const handleModeChange = (e: CustomEvent<UserMode>) => {
      setUserModeState(e.detail);
    };

    window.addEventListener(USER_MODE_CHANGE_EVENT, handleModeChange as EventListener);
    return () => {
      window.removeEventListener(USER_MODE_CHANGE_EVENT, handleModeChange as EventListener);
    };
  }, []);

  const setUserMode = useCallback((mode: UserMode) => {
    setUserModeState(mode);
    StorageService.set('user_mode', mode);
    console.log(`🎯 User mode changed to: ${mode}`);

    // Sync to database if authenticated
    userPreferencesService.setPreference('userMode', mode);

    // Dispatch event to sync across all components
    window.dispatchEvent(new CustomEvent(USER_MODE_CHANGE_EVENT, { detail: mode }));
  }, []);

  return [userMode, setUserMode];
}
